import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { AnimatedModal, modalPanelClass } from "../../components/AnimatedModal";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import {
  HiOutlineXMark,
  HiOutlineEnvelope,
  HiOutlineCheckCircle,
} from "react-icons/hi2";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors disabled:opacity-70";

const InviteUser: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const { isAdmin, organizationId } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleClose = () => {
    setEmail("");
    setError("");
    setSuccess("");
    onClose();
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!email) {
      setError(t("pleaseEnterEmail"));
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (organizationId) {
        const invitationLink = `${window.location.origin}/signup?invitationCode=${uuidv4()}`;
        const response = await fetch("/.netlify/functions/sendInvite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, invitationLink, organizationId }),
        });

        if (response.ok) {
          setSuccess(t("invitationSent"));
          setEmail("");
        } else {
          const errorResponse = await response.json();
          setError(
            `${t("failedToSendInvitation")}: ${errorResponse.error || ""}`,
          );
        }
      } else {
        setError(t("organizationNotFound"));
      }
    } catch (err) {
      setError(t("failedToSendInvitation"));
      console.error(t("errorSendingInvitation"), err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || (!isAdmin && !organizationId)) {
    return null;
  }

  const inputBase =
    "w-full px-4 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";

  return (
    <AnimatedModal isOpen={isOpen} onClose={handleClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineEnvelope className="w-6 h-6" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {t("inviteUser")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("inviteUserSubtitle")}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleInvite}>
          <div className="p-5 sm:p-6 space-y-4">
            <div>
              <label
                htmlFor="invite-email"
                className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2"
              >
                {t("userEmail")}
              </label>
              <input
                type="email"
                id="invite-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("enterUserEmail")}
                maxLength={100}
                required
                disabled={isSubmitting}
                className={inputBase}
              />
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-xl border border-red-200/70 dark:border-red-800/50 bg-red-50/80 dark:bg-red-900/20 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-emerald-200/70 dark:border-emerald-800/50 bg-emerald-50/80 dark:bg-emerald-900/20 text-sm text-emerald-700 dark:text-emerald-300">
                <HiOutlineCheckCircle className="w-5 h-5 shrink-0" />
                {success}
              </div>
            )}
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-900/20 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className={glassBtnMuted}
            >
              {t("close")}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${glassBtn} sm:ml-auto`}
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
                  {t("sending")}
                </>
              ) : (
                t("sendInvite")
              )}
            </button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
};

export default InviteUser;
